import {
  API_URL,
  put,
  takeLeading,
  debug,
  createAction,
  request,
  SagaIterator,
  call,
  select,
  delay,
  race,
  take,
  eventChannel,
  EventChannel,
} from '../../lib'
import {BaseRequestResult} from '../../lib/request'
import {RefreshTokenAction, REFRESH_REQUEST_TIMEOUT_MS} from '../../lib/auth/refresh_token_action'
import {isLogoutPending, recordLogoutReply} from '../../lib/auth/logout_pending'
import Instrumentation from '../../instrumentation'

import {
  doAUTH_SET,
  doREQUEST_ERROR,
  doREQUEST_COMPLETE,
  doAUTH_REFRESHING,
  doAUTH_REFRESHING_FAILURE,
  doAUTH_REFRESH_UNAVAILABLE,
  doPROFILE_SET,
  doSETTINGS_SET,
  doPROJECT_SETTINGS_LIST_SET,
  doFAVORITE_LIST_SET,
  doIDENTITY_SET,
  doSESSION_END_REASON_SET,
  AUTH_REFRESH_RETRY_NOW,
  AUTH_REFRESHING_FAILURE,
  SessionEndReason,
} from '../state'
import {RefreshOutcome, classifyRefreshResult, pickSessionEndReason, refreshRetryDelayMs} from './refresh_outcome'

export const REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT = 'REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT'
export const doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT = createAction(REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT)

export const requestIdentityRefreshTokenSubmitActionCreators = {
  doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT,
}

const REFRESH_URL = '/v1/web/identity-refresh-token-submit'
const LOGOUT_URL = '/v1/web/identity-logout'

// `createAppStore`'s saga `onError` re-throws, which under `takeLeading` would kill the one
// watcher and leave the tab unable to refresh for the rest of its life. So the worker swallows
// its own exceptions and pushes them to the same Faro sink by hand.
const reportRefreshError = (error: unknown) => {
  try {
    Instrumentation.shared.perform((api) => {
      api.pushError(error as Error, {
        context: {
          info: JSON.stringify({saga: 'requestIdentityRefreshTokenSubmit'}),
          source: 'Saga',
        },
      })
    })
  } catch {
    // reporting the failure must never become the reason the cycle stops
  }
}

// `online` is a hint to try again now, never proof that the API is reachable, so winning
// the race does not reset the schedule. One channel per cycle, closed in the worker's `finally`.
const createOnlineChannel = () =>
  eventChannel<string>((emit) => {
    const listener = () => emit('online')
    window.addEventListener('online', listener)
    return () => window.removeEventListener('online', listener)
  })

interface RefreshAttemptInput {
  payload: any
  setRequestResult: any
  hadToken: boolean
}

// Exactly one of AUTH_SET, AUTH_REFRESHING_FAILURE or AUTH_REFRESH_UNAVAILABLE follows every
// attempt, so anything waiting on the outcome actions always hears back.
function* runRefreshAttempt({
  payload,
  setRequestResult,
  hadToken,
}: RefreshAttemptInput): Generator<any, RefreshOutcome, any> {
  let emitted: RefreshOutcome | null = null

  try {
    // a logout this tab (or a sibling tab) could not deliver outranks any refresh. Checked
    // before EVERY attempt, so a sign-out during the retry wait is honoured on the next tick.
    // The marker retires on the server's definitive answer -- a 2xx, 401 or 404, see
    // `isDefinitiveLogoutReply` -- and on nothing else, so a still-unreachable backend leaves the
    // next boot to try again.
    if (yield call(isLogoutPending)) {
      debug('saga*requestIdentityRefreshTokenSubmit honouring a pending logout')

      const logoutResult: BaseRequestResult = yield request({
        method: 'post',
        url: `${API_URL}${LOGOUT_URL}`,
        timeout: REFRESH_REQUEST_TIMEOUT_MS,
      })
      yield call(recordLogoutReply, logoutResult?.statusCode)

      if (hadToken) {
        const at = Date.now()
        yield put(doSESSION_END_REASON_SET({reason: SessionEndReason.SessionEnded, at}))
      }

      // no REQUEST_ERROR: a deliberate sign-out is not an app error to surface, and nothing may
      // wait on a refresh requestId anyway
      yield put(doAUTH_REFRESHING_FAILURE())
      emitted = RefreshOutcome.Terminal
      return RefreshOutcome.Terminal
    }

    yield put(doAUTH_REFRESHING())

    const result: BaseRequestResult = yield call(() => RefreshTokenAction.instance.execute())
    const outcome = classifyRefreshResult(result)

    if (outcome === RefreshOutcome.Transient) {
      debug('saga*requestIdentityRefreshTokenSubmit transient refresh failure', result?.statusCode)
      return RefreshOutcome.Transient
    }

    if (outcome === RefreshOutcome.Terminal) {
      debug('saga*requestIdentityRefreshTokenSubmit terminal refresh failure', result?.statusCode)

      // only a tab that actually held a token can say "your session ended"; a fresh visit
      // that was never signed in shows nothing. Written before the auth entry is dropped, since
      // both reducers persist into the same session-storage record.
      if (hadToken) {
        const lastSecurityChange = yield select((state: any) => state?.session?.lastSecurityChange ?? null)
        const at = Date.now()
        yield put(doSESSION_END_REASON_SET({reason: pickSessionEndReason(lastSecurityChange, at), at}))
      }

      yield put(doAUTH_REFRESHING_FAILURE())
      emitted = RefreshOutcome.Terminal

      yield put(
        doREQUEST_ERROR(
          {
            fromType: REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT,
            fromPayload: payload,
            requestId: payload.requestId,
            url: REFRESH_URL,
            ...result,
          },
          setRequestResult,
        ),
      )

      return RefreshOutcome.Terminal
    }

    const {
      profile,
      settings,
      projectSettingsList,
      workspaceFavoriteList,
      accessToken,
      accessTokenExpiresInSeconds,
      refreshTokenExpiresInSeconds,
      identity,
    } = result.payload

    yield put(
      doAUTH_SET({
        accessToken,
        accessTokenExpiresInSeconds,
        refreshTokenExpiresInSeconds,
      }),
    )
    emitted = RefreshOutcome.Success

    yield put(doPROFILE_SET(profile || {}))
    if (settings) yield put(doSETTINGS_SET(settings))
    yield put(doPROJECT_SETTINGS_LIST_SET(projectSettingsList || []))
    yield put(doFAVORITE_LIST_SET(workspaceFavoriteList || []))
    if (identity) yield put(doIDENTITY_SET(identity))

    yield put(
      doREQUEST_COMPLETE(
        {
          fromType: REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT,
          requestId: payload.requestId,
          ...result,
        },
        setRequestResult,
      ),
    )

    return RefreshOutcome.Success
  } catch (error) {
    // a reducer guard on a half-shaped 200, a serialization check, anything: keep the token,
    // keep the watcher, and let the schedule try again
    debug('saga*requestIdentityRefreshTokenSubmit unexpected error', error)
    reportRefreshError(error)
    return emitted ?? RefreshOutcome.Transient
  }
}

export function* requestIdentityRefreshTokenSubmit({payload, setRequestResult}: any): SagaIterator {
  debug('saga*requestIdentityRefreshTokenSubmit')

  // captured once for the whole cycle: whether this tab ever held a token decides whether a
  // terminal outcome is worth explaining on the login page
  const hadToken = Boolean(yield select((state: any) => state?.auth?.accessToken))

  const onlineChannel: EventChannel<string> = yield call(createOnlineChannel)

  try {
    let attempt = 0

    for (;;) {
      attempt += 1

      const outcome = yield* runRefreshAttempt({payload, setRequestResult, hadToken})
      if (outcome !== RefreshOutcome.Transient) return

      const wait = refreshRetryDelayMs(attempt)
      // the transient action is the only thing dispatched -- no REQUEST_ERROR, so a long
      // outage cannot grow one request-state entry per retry
      yield put(doAUTH_REFRESH_UNAVAILABLE({attempt, nextRetryAt: Date.now() + wait}))

      const {sessionEnded} = yield race({
        // the schedule, the browser saying it is back, and the user's "Retry now"
        scheduled: delay(wait),
        online: take(onlineChannel),
        retryNow: take(AUTH_REFRESH_RETRY_NOW),
        // a 401-triggered join: an ordinary 401 proves the API is reachable, so go now. The 10s
        // request timeout bounds how often user traffic can force an attempt.
        rejoin: take(REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT),
        // this cycle only emits AUTH_REFRESHING_FAILURE from inside an attempt, so one arriving
        // here means the session was ended elsewhere (a sibling tab's sign-out). Retrying after
        // that would send this tab's own logout and could land on top of a fresh login.
        sessionEnded: take(AUTH_REFRESHING_FAILURE),
      })
      if (sessionEnded) return
    }
  } finally {
    onlineChannel.close()
  }
}

export function* identityRefreshTokenSubmitSaga() {
  // one cycle at a time. A dispatch that arrives while a cycle runs is dropped, and its
  // dispatcher waits on the outcome actions instead of on a requestId.
  yield takeLeading(REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT, requestIdentityRefreshTokenSubmit)
}
